import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm, Controller, Resolver } from 'react-hook-form';
import InputNumberFormat from 'react-number-format';

import { yupResolver } from '@hookform/resolvers/yup';
import { InfoCircleFill as InfoCircleIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Search as SearchIcon } from '@styled-icons/fa-solid/Search';
import { isCPFOrCNPJ } from 'brazilian-values';
import { api } from 'services/api';
import { getErrors } from 'services/axios';
import { toast } from 'shared/toast';
import * as yup from 'yup';

import {
  Button,
  Icon,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputRightAddon,
  Select,
  Tooltip,
  InputGroup,
  IconButton,
  Textarea,
} from '@chakra-ui/react';

import OwnSelect from 'components/OwnSelect';

import { CEPServiceResponse } from 'types/address';
import { Client, City, State } from 'types/client';
import { ServiceError } from 'types/service';

interface FormProps {
  onSubmit(data: Client): void;
  initialData?: Client;
  saving?: boolean;
  serviceErrors?: ServiceError;
  formId?: string;
  showSubmitButton?: boolean;
  submitLabel?: string;
}

const FormRegister: React.FC<FormProps> = ({
  initialData,
  onSubmit,
  saving,
  serviceErrors,
  formId,
  showSubmitButton = true,
  submitLabel = 'Salvar',
}) => {
  const validationSchema = yup.object().shape({
    type_person: yup.string().required('Tipo é obrigatório.'),
    name: yup
      .string()
      .nullable()
      .when('type_person', {
        is: 'physical',
        then: yup.string().required('Nome é obrigatório.'),
      }),
    document: yup
      .string()
      .required('Documento é obrigatório.')
      .test('valid-document', 'Documento inválido', (document) =>
        isCPFOrCNPJ(document || ''),
      ),
    email: yup.string().nullable().email('E-mail precisa ser um email válido.'),
    corporate_name: yup
      .string()
      .nullable()
      .when('type_person', {
        is: 'legal',
        then: yup.string().required('Razão social é obrigatório'),
      }),
    fantasy_name: yup
      .string()
      .nullable()
      .when('type_person', {
        is: 'L',
        then: yup.string().required('Nome fantasia é obrigatório.'),
      }),
    address: yup.string().nullable(),
    number: yup
      .number()
      .integer('Número precisa ser inteiro.')
      .positive('Número precisa ser positivo.')
      .transform((value, original) => (original === '' ? null : value))
      .nullable(),
    complementary: yup.string(),
    neighborhood: yup.string(),
    state_id: yup.string(),
    city_id: yup.string(),
    cep: yup.string(),
  });

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
  } = useForm<Client>({
    defaultValues: initialData,
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<Client>,
  });

  const [isFindingCEP, setIsFindingCEP] = useState(false);

  const [stateSelected, setStateSelected] = useState(
    initialData?.state_id ? Number(initialData?.state_id) : '',
  );

  const [citySelected, setCitySelected] = useState(
    initialData?.city_id ? Number(initialData?.city_id) : '',
  );

  const [stateList, setStateList] = useState<State[]>([]);
  const [isFindingState, setIsFindingState] = useState(false);

  const [cityList, setCityList] = useState<City[]>([]);
  const [isFindingCity, setIsFindingCity] = useState(false);

  const statesToSelect = useMemo(
    () =>
      stateList.map((state) => ({
        value: state.id,
        label: state.name,
      })),
    [stateList],
  );

  const citiesToSelect = useMemo(
    () =>
      cityList.map((city) => ({
        value: city.id,
        label: city.name,
      })),
    [cityList],
  );

  const handleChangeState = useCallback(async (stateId: number) => {
    setIsFindingCity(true);
    try {
      const { data: responseCities } = await api.get(`/state/${stateId}/city`);

      const citiesLoaded = responseCities.data;

      setCityList(citiesLoaded);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar a lista de cidades.',
        status: 'error',
      });
    } finally {
      setIsFindingCity(false);
    }
  }, []);

  const getStates = useCallback(async () => {
    setIsFindingState(true);
    try {
      const { data: responseStates } = await api.get(`/state`);
      const states = responseStates.data;
      setStateList(states);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar a lista de estados.',
        status: 'error',
      });
    } finally {
      setIsFindingState(false);
    }
  }, []);

  const handleFindCEP = useCallback(
    async (e) => {
      e.preventDefault();

      const { cep } = getValues();

      if (!cep) return;

      setIsFindingCEP(true);

      const toastId = toast({
        status: 'info',
        description: 'Pesquisando CEP...',
        duration: null,
      });

      try {
        const { data: cepData } = await api.get<CEPServiceResponse>(
          `/zipcode/${cep}`,
        );

        const cepInfo = cepData.data;

        handleChangeState(Number(cepInfo.city.state.id));

        setStateSelected(Number(cepInfo.city.state.id));
        setCitySelected(Number(cepInfo.city.id));

        setValue('state_id', cepInfo.city.state.id);
        setValue('city_id', cepInfo.city.id);
        setValue('address', cepInfo.street);
        setValue('complement', cepInfo.complement);
        setValue('district', cepInfo.neighborhood);

        toast({ description: 'CEP encontrado!', status: 'success' });
      } catch {
        toast({ description: 'CEP não encontrado.', status: 'error' });
      } finally {
        setIsFindingCEP(false);
        if (toastId) toast.close(toastId);
      }
    },
    [getValues, setValue, handleChangeState],
  );

  useEffect(() => {
    getStates();
  }, [getStates]);

  useEffect(() => {
    if (initialData?.state_id) {
      handleChangeState(initialData?.state_id);
    }
    /* eslint-disable-next-line */
  }, []);

  const watchPersonType = watch('type_person');

  const handleOnSubmit = useCallback(
    (data: Client) => {
      onSubmit({
        ...data,
        document_type: data.type_person === 'physical' ? 'cpf' : 'cnpj',
        zip_code: data.cep || '',
        ...(data.type_person === 'physical'
          ? { cpf: data.document }
          : { cnpj: data.document }),
      });
    },
    [onSubmit],
  );

  return (
    <Flex
      w="100%"
      as="form"
      id={formId}
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      sx={{
        '> *': {
          marginY: 1,
        },
      }}
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      <Grid
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={2}
        marginTop={4}
        width="100%"
      >
        <FormControl isInvalid={!!errors?.type_person}>
          <FormLabel>
            Tipo{' '}
            <Tooltip
              label="Informe o tipo do documento entre Pessoa Física (CPF) ou Pessoa Jurídica (CNPJ)"
              hasArrow
            >
              <Icon as={InfoCircleIcon} color="grey" />
            </Tooltip>
          </FormLabel>

          <Select
            defaultValue={initialData?.type_person}
            {...register('type_person')}
          >
            <option value="physical">Pessoa física</option>
            <option value="legal">Pessoa jurídica</option>
          </Select>

          <FormErrorMessage>{errors.type_person?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.document}>
          <FormLabel>{watchPersonType === 'legal' ? 'CNPJ' : 'CPF'}</FormLabel>

          <Controller
            render={({ field }) => (
              <InputNumberFormat
                {...field}
                type="tel"
                displayType="input"
                placeholder="Ex: 99.999.999/0001-99"
                format={
                  watchPersonType === 'legal'
                    ? '##.###.###/####-##'
                    : '###.###.###-##'
                }
                mask="_"
                defaultValue={initialData?.document}
                onValueChange={(v) => setValue('document', v.formattedValue)}
                customInput={Input}
              />
            )}
            name="document"
            control={control}
          />

          <FormErrorMessage>{errors.document?.message}</FormErrorMessage>
        </FormControl>
      </Grid>

      {watchPersonType === 'physical' && (
        <FormControl isInvalid={!!errors?.name}>
          <FormLabel>Nome completo</FormLabel>

          <Input
            type="text"
            {...register('name', { required: true })}
            placeholder="Nome"
          />

          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>
      )}

      {watchPersonType === 'legal' && (
        <Grid
          templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          columnGap={2}
          rowGap={2}
          marginTop={4}
          width="100%"
        >
          <FormControl isInvalid={!!errors?.corporate_name}>
            <FormLabel>Razão Social</FormLabel>

            <Input
              type="text"
              {...register('corporate_name', { required: true })}
              placeholder="Razão social"
            />

            <FormErrorMessage>
              {errors.corporate_name?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.fantasy_name}>
            <FormLabel>Nome fantasia</FormLabel>

            <Input
              type="text"
              {...register('fantasy_name', { required: true })}
              placeholder="Nome fantasia"
            />

            <FormErrorMessage>{errors.fantasy_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>
      )}

      <Grid
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={2}
        marginTop={4}
        width="100%"
      >
        <FormControl isInvalid={!!errors?.phone}>
          <FormLabel>Telefone (fixo)</FormLabel>

          <Controller
            render={({ field }) => (
              <InputNumberFormat
                {...field}
                type="tel"
                displayType="input"
                format="(##) ####-####"
                placeholder="(00) 0000-0000"
                mask="_"
                defaultValue={initialData?.phone}
                onValueChange={(v) => setValue('phone', v.formattedValue)}
                formNoValidate
                customInput={Input}
              />
            )}
            name="phone"
            control={control}
          />

          <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.cell_phone}>
          <FormLabel>Telefone (celular / WhatsApp)</FormLabel>

          <Controller
            render={({ field }) => (
              <InputNumberFormat
                {...field}
                type="tel"
                displayType="input"
                format="(##) #####-####"
                placeholder="(00) 00000-0000"
                mask="_"
                defaultValue={initialData?.cell_phone}
                onValueChange={(v) => setValue('cell_phone', v.formattedValue)}
                formNoValidate
                customInput={Input}
              />
            )}
            name="cell_phone"
            control={control}
          />

          <FormErrorMessage>{errors.cell_phone?.message}</FormErrorMessage>
        </FormControl>
      </Grid>

      <Grid
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={2}
        marginTop={4}
        width="100%"
      >
        <FormControl isInvalid={!!errors?.email || !!serviceErrors?.email}>
          <FormLabel>Email</FormLabel>

          <Input
            type="email"
            placeholder="exemplo@exemplo.com"
            {...register('email')}
          />

          <FormErrorMessage>
            {errors.email?.message || getErrors(serviceErrors?.email)}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.contact}>
          <FormLabel>Contato</FormLabel>

          <Input type="text" {...register('contact')} />

          <FormErrorMessage>{errors.contact?.message}</FormErrorMessage>
        </FormControl>
      </Grid>

      <Grid
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={2}
        marginTop={4}
        width="100%"
      >
        <FormControl isInvalid={!!errors.cep}>
          <FormLabel htmlFor="cep">CEP</FormLabel>
          <InputGroup>
            <Controller
              render={({ field }) => (
                <InputNumberFormat
                  {...field}
                  type="tel"
                  displayType="input"
                  format="#####-###"
                  placeholder="00000-000"
                  mask="_"
                  onValueChange={(v) => setValue('cep', v.formattedValue)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleFindCEP(e);
                    }
                  }}
                  customInput={Input}
                />
              )}
              name="cep"
              control={control}
            />

            <InputRightAddon px={0}>
              <IconButton
                height="100%"
                colorScheme="blackAlpha"
                aria-label="Pesquisar CEP"
                title="Pesquisar CEP"
                borderTopLeftRadius="0"
                borderBottomLeftRadius="0"
                onClick={handleFindCEP}
                onBlur={handleFindCEP}
                isLoading={isFindingCEP}
                width="50px"
              >
                <Icon as={SearchIcon} />
              </IconButton>
            </InputRightAddon>
          </InputGroup>

          <FormErrorMessage>{errors.cep?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.state_id || !!serviceErrors?.state_id}>
          <FormLabel>Estado</FormLabel>

          <OwnSelect
            placeholder={isFindingState ? 'Carregando' : 'Selecione'}
            options={statesToSelect}
            isInvalid={!!errors.state_id || !!serviceErrors?.state_id}
            defaultValue={[initialData?.state_id]}
            value={statesToSelect.filter((s) => s.value === stateSelected)}
            isDisabled={isFindingState || statesToSelect.length === 0}
            isLoading={isFindingState}
            onChange={(selected) => {
              setStateSelected(Number(selected?.value));
              setValue('state_id', Number(selected?.value));
              handleChangeState(Number(selected?.value));

              if (selected && errors.state_id) {
                clearErrors('state_id');
              }
            }}
          />

          <FormErrorMessage>
            {errors.state_id?.message || getErrors(serviceErrors?.state_id)}
          </FormErrorMessage>

          <input
            type="hidden"
            value={stateSelected}
            {...register('state_id', { required: true })}
          />
        </FormControl>

        <FormControl isInvalid={!!errors.city_id || !!serviceErrors?.city_id}>
          <FormLabel>Cidade</FormLabel>

          <OwnSelect
            placeholder={isFindingCity ? 'Carregando' : 'Selecione'}
            options={citiesToSelect}
            isInvalid={!!errors.city_id || !!serviceErrors?.city_id}
            value={citiesToSelect.filter((c) => c.value === citySelected)}
            isDisabled={isFindingCity || citiesToSelect.length === 0}
            isLoading={isFindingCity}
            onChange={(selected) => {
              setCitySelected(Number(selected?.value));
              setValue('city_id', Number(selected?.value));

              if (selected && errors.city_id) {
                clearErrors('city_id');
              }
            }}
          />

          <FormErrorMessage>
            {errors.city_id?.message || getErrors(serviceErrors?.city_id)}
          </FormErrorMessage>

          <input
            type="hidden"
            value={citySelected}
            {...register('city_id', { required: true })}
          />
        </FormControl>
      </Grid>

      <Grid
        templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
        columnGap={2}
        rowGap={2}
        marginTop={4}
        width="100%"
      >
        <FormControl isInvalid={!!errors?.address || !!serviceErrors?.address}>
          <FormLabel>Endereço</FormLabel>

          <Input
            type="text"
            placeholder="Ex: Rua Padre João"
            {...register('address', { required: true })}
          />

          <FormErrorMessage>
            {errors.address?.message || getErrors(serviceErrors?.address)}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.number}>
          <FormLabel>Número</FormLabel>

          <Input
            type="number"
            placeholder="Ex: 123"
            {...register('number', { required: false })}
          />
          <FormErrorMessage>{errors.number?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.complement}>
          <FormLabel>Complemento</FormLabel>

          <Input
            type="text"
            placeholder="Ex: Apartamento 10"
            {...register('complement', { required: false })}
          />

          <FormErrorMessage>{errors.complement?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.district}>
          <FormLabel htmlFor="name">Bairro</FormLabel>
          <Input
            type="text"
            placeholder="Ex: Vila Mariana"
            {...register('district', { required: true })}
          />
          <FormErrorMessage>{errors.district?.message}</FormErrorMessage>
        </FormControl>
      </Grid>

      <FormControl marginTop="4" isInvalid={!!errors?.observation}>
        <FormLabel>Observação</FormLabel>
        <Textarea
          resize="none"
          backgroundColor="white"
          {...register('observation', { required: false })}
        />
        <FormErrorMessage>{errors.observation?.message}</FormErrorMessage>
      </FormControl>

      {showSubmitButton && (
        <Button
          width="100%"
          mt="4"
          colorScheme="blue"
          isLoading={saving}
          isDisabled={saving}
          type="submit"
        >
          {submitLabel}
        </Button>
      )}
    </Flex>
  );
};

export default FormRegister;
